import type { DiffEntry, ReviewRecord } from '@/composables/usePolicyEditPending';
import type { ReviewPreviewResponse, SubitemReviewChange } from '@/types/ai';
import { subitemLabel } from '@/types/PolicySublimit';
import { formatCurrency } from '@/utilities/Money';

// Adapt the review preview into the shape consumed by PolicyEditReviewPanel: a policy
// record, an account record, and one record per coverage line (grouped by coverage type).
// Empty values map to '' so the panel renders its em-dash placeholder.
const toReviewValue = (value: string | number | boolean | null): string => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

// Every line-level override the preview diffs is a dollar amount.
const toLineReviewValue = (value: string | number | boolean | null): string =>
  typeof value === 'number' ? formatCurrency(value) : toReviewValue(value);

export const hasReviewChanges = (preview: ReviewPreviewResponse | null): boolean =>
  !!preview &&
  [preview.changes, preview.accountChanges, preview.lineChanges, preview.lineStructureChanges, preview.subitemChanges].some(
    (list) => (list?.length ?? 0) > 0,
  );

// Full precision rather than the Override modal's compactMoney: an update is only previewed
// when the value changes, and compacting can render both sides of it identically.
// The index keeps keys unique if two extracted lines of one coverage type carry the same type.
const subitemDiff = (change: SubitemReviewChange, index: number): DiffEntry => ({
  field: `${change.kind}:${change.typeId}:${index}`,
  label: subitemLabel(change.kind, change.typeName),
  oldValue: change.currentValue === null ? '' : formatCurrency(change.currentValue),
  newValue: formatCurrency(change.newValue),
});

export const buildReviewRecords = (preview: ReviewPreviewResponse | null): ReviewRecord[] => {
  if (!preview) return [];

  const records: ReviewRecord[] = [];
  const uniqPolicy = String(preview.policyId ?? '');
  // Sub-items join the card that already represents their coverage line, so a reviewer sees a
  // line's core fields and its sub-items together.
  const lineRecordByCoverage = new Map<string, ReviewRecord>();

  if (preview.changes?.length) {
    records.push({
      ref: { key: 'policy', type: 'policy', uniqPolicy, label: preview.policyNumber || 'Policy' },
      diffs: preview.changes.map((c) => ({
        field: c.field,
        label: c.field,
        oldValue: toReviewValue(c.currentValue),
        newValue: toReviewValue(c.newValue),
        restricted: c.restricted,
        restrictionReason: c.restrictionReason,
      })),
    });
  }

  if (preview.accountChanges?.length) {
    records.push({
      ref: { key: 'account', type: 'account', uniqPolicy, label: 'Account' },
      diffs: preview.accountChanges.map((c) => ({
        field: c.field,
        label: c.field,
        oldValue: toReviewValue(c.currentValue),
        newValue: toReviewValue(c.newValue),
      })),
    });
  }

  if (preview.lineChanges?.length) {
    const byCoverage = new Map<string, DiffEntry[]>();
    for (const lc of preview.lineChanges) {
      const coverageType = lc.coverageType || 'Coverage line';
      const diffs = byCoverage.get(coverageType) ?? [];
      diffs.push({
        field: lc.field,
        label: lc.field,
        oldValue: toLineReviewValue(lc.currentValue),
        newValue: toLineReviewValue(lc.newValue),
        restricted: lc.restricted,
        restrictionReason: lc.restrictionReason,
      });
      byCoverage.set(coverageType, diffs);
    }
    for (const [coverageType, diffs] of byCoverage) {
      const record: ReviewRecord = {
        ref: { key: `line:${coverageType}`, type: 'line', uniqPolicy, uniqLine: coverageType, label: coverageType },
        diffs,
      };
      records.push(record);
      lineRecordByCoverage.set(coverageType, record);
    }
  }

  if (preview.lineStructureChanges?.length) {
    const noMatchReason = 'No matching policy line on this policy — the line will not import';
    for (const sc of preview.lineStructureChanges) {
      const coverageType = sc.coverageType || 'Coverage line';
      const retargetsLine = sc.operation === 'add' || sc.operation === 'recategorize';
      let label = coverageType;
      let diff: DiffEntry;
      switch (sc.operation) {
        case 'add':
          label = `${coverageType} (new line)`;
          diff = { field: 'line', label: 'coverage line', oldValue: '', newValue: 'Added' };
          break;
        case 'delete':
          diff = { field: 'line', label: 'coverage line', oldValue: 'Present', newValue: 'Removed' };
          break;
        case 'recategorize':
          diff = {
            field: 'coverageType',
            label: 'coverage type',
            oldValue: sc.previousCoverageType ?? '',
            newValue: coverageType,
          };
          break;
        default:
          diff = { field: 'line', label: 'coverage line', oldValue: 'Removed', newValue: 'Restored' };
      }
      if (retargetsLine && !sc.hasMatchingPolicyLine) {
        diff.restricted = true;
        diff.restrictionReason = noMatchReason;
      }
      const record: ReviewRecord = {
        ref: { key: `line:structure-${sc.lineKey}`, type: 'line', uniqPolicy, uniqLine: sc.lineKey, label },
        diffs: [diff],
      };
      records.push(record);
      if ((retargetsLine || sc.operation === 'restore') && !lineRecordByCoverage.has(coverageType)) {
        lineRecordByCoverage.set(coverageType, record);
      }
    }
  }

  for (const [index, change] of (preview.subitemChanges ?? []).entries()) {
    const coverageType = change.coverageType || 'Coverage line';
    let record = lineRecordByCoverage.get(coverageType);
    if (!record) {
      record = {
        ref: { key: `line:${coverageType}`, type: 'line', uniqPolicy, uniqLine: coverageType, label: coverageType },
        diffs: [],
      };
      records.push(record);
      lineRecordByCoverage.set(coverageType, record);
    }
    record.diffs.push(subitemDiff(change, index));
  }

  return records;
};
